












































































import { defineComponent, toRefs, reactive, ref, watch, computed, onBeforeUnmount, Ref, onMounted } from '@vue/composition-api';
import {
    KwaiPlayer,
    KwaiPlayerBar,
    KwaiPlayerCenterState,
    KwaiPlayerControlsFullscreen,
    KwaiPlayerControlsPlay,
    KwaiPlayerControlsProgressBar,
    KwaiPlayerControlsProgressTime,
} from '@ks/player';
import '@ks/player/dist/index.esm.css';
import PlayerTime from './plugins/player-time.vue';
import KwaiPlayerControlsVolume from './plugins/player-volume.vue';
import Loading from '@/components/loading/index.vue';
import AutoPlay from '@/components/autoplay-button/autoplay-item.vue';
import PlayerError from './plugins/player-error.vue';
import PlayerModel from './model';
import { useApolloModel } from 'vue-apollo-model';
import { isIE, useBroadcast, useBroadCastStream, isSafari } from '@/common/utils';
import { useLog } from '@/common/log';
import { useRealTimeReport, useSummaryReport, usePlayKeyDownEvent, useDegradeTips, useVideoLoad, playConfig } from './player';
import KwaiPlayerKernel from '@ks-video/kwai-player-kernel';

export default defineComponent({
    components: {
        KwaiPlayer,
        KwaiPlayerBar,
        KwaiPlayerCenterState,
        KwaiPlayerControlsFullscreen,
        KwaiPlayerControlsPlay,
        KwaiPlayerControlsProgressBar,
        KwaiPlayerControlsProgressTime,
        KwaiPlayerControlsVolume,
        PlayerTime,
        Loading,
        PlayerError,
        AutoPlay,
    },
    props: {
        src: {
            type: [ Object, String ],
        },
        muted: {
            type: Boolean,
            default: true,
        },
        live: {
            type: Boolean,
            default: false,
        },
        config: {
            type: Object,
            default: (() => {
                return {
                    timeKeyDown: true,
                    volumeKeyDown: true,
                };
            }),
        },
        fullbackPlayUrl: {
            type: String,
            default: '',
        },
        canPresetVolume: {
            type: Boolean,
            default: true,
        },
        initVolumeConfig: {
            type: Object,
            default: () => {
                return {
                    volume: 0.5,
                    setLocal: true,
                    localName: 'visionRecoVolume',
                };
            },
        },
        poster: {
            type: String,
            default: '',
        },
        fullscreenComponentName: {
            type: String,
        },
        initFullscreen: {
            type: Boolean,
            default: false,
        },
        fullscreenTipDelay: {
            type: Object,
        },
        index: {
            type: Number,
            default: 0,
        },
        activeIndex: {
            type: Number,
            default: 0,
        },
        canChangeVolume: {
            type: Boolean,
            default: true,
        },
    },
    setup(props, { emit }) {
        const player = ref<InstanceType<typeof KwaiPlayer> | null>(null);
        const playModel = useApolloModel(PlayerModel);
        const kernelConfig = reactive<{
            nativeFirst: boolean;
            startFromSec: number;
            isLive: boolean;
        }>({
            nativeFirst: true,
            startFromSec: 0,
            isLive: props.live,
        });
        const src = computed(() => props.src);
        const fullbackPlayUrl = computed(() => props.fullbackPlayUrl);
        const muted = computed(() => props.muted);
        const playerTime = ref<InstanceType<typeof PlayerTime> | null>(null);
        const canShowBackPoster = !isSafari();
        const isRecoVideo = ref(true);
        const isActiveVideo = computed(() => props.index === props.activeIndex);

        const state = reactive({
            playStatus: false,
            isEnd: false,
            showError: false,
            isFullscreen: false,
            errorInfo: {
                errorTitle: '无法连接网络，请稍后再试',
                retryText: '点击重试',
            },
        });
        const ie = computed(() => isIE());
        const log = useLog();
        const broadcast = useBroadcast();

        const showDegradeTips = useDegradeTips(playConfig, {
            state,
            live: props.live,
        });

        useBroadCastStream('change-video', data => {
            useSummaryReport(
                {
                    // @ts-ignore
                    summaryData: data.summaryQosData,
                    // @ts-ignore
                    waitingTime: data.waitingTime,
                    // @ts-ignore
                    pageLoadTime: data.pageLoadTime,
                },
                log,
            );

            if (showDegradeTips.value && !props.live) {
                showDegradeTips.value = false;
            }
        });

        useBroadCastStream('reload-reco-video', (data: { playerIndex: number }) => {
            if (data.playerIndex === props.index) {
                player.value?.play();
            }
        });

        function resetVideo() {
            player.value?.resetVideo();
        }

        const { loadVideo } = useVideoLoad(playModel, player as Ref<InstanceType<typeof KwaiPlayer> | null>, {
            showDegradeTips,
            muted,
            isRecoVideo,
        });
        onMounted(() => {
            watch(() => props.activeIndex, val => {
                if (val === props.index) {
                    loadVideo(src.value, fullbackPlayUrl.value, true);
                } else {
                    resetVideo();
                }
                if (!muted.value) {
                    broadcast('change-volume', 0.5);
                }
            }, {
                immediate: true,
            });
        });


        function useVideoPlay() {
            if (props.live || !isActiveVideo.value) {
                return;
            }
            if (player.value?.video?.paused) {
                player.value?.play();
            } else {
                player.value?.pause();
            }
        }
        function handleSeeked() {
            if (player.value?.video?.paused) {
                player.value?.play();
            }
        }
        usePlayKeyDownEvent(() => useVideoPlay(), useBroadCastStream);
        onMounted(() => {
            state.isFullscreen = props.initFullscreen;
        });
        onBeforeUnmount(() => {
            resetVideo();
        });

        function handlePause() {
            console.log('pause---');
        }

        function handlePlay() {
            state.isEnd = false;
            state.showError = false;
        }

        function handleLoadStart() {
            if (isActiveVideo.value) {
                playModel.kernelLoadedTime = (+new Date());
                playModel.kernelInstance = player.value?.kernelInstance;
                useRealTimeReport(playModel.kernelInstance, log);
            }
        }

        function handleEnded() {
            emit('ended');
            state.isEnd = true;
        }

        function handleError(e: any) {
            // 防盗链
            if (e.code === 1003 || e.code === 1203) {
                state.showError = false;
                kernelConfig.startFromSec = playerTime.value?.currentTime || 0;
                return broadcast('refetch-video', {});
            }
            if (e instanceof Event) {
                state.showError = false;
                return;
            }
            if (playModel.ignoreErrorList.find(ignore => e.message && e.message.indexOf(ignore) > -1)) {
                state.showError = false;
                return;
            }
            state.showError = true;
        }

        function fullscreenChange(isFullscreen: boolean) {
            state.isFullscreen = isFullscreen;
            emit('fullscreen-change', isFullscreen);
            log({
                type: 'CLICK',
                event_name: 'FULLSCREEN_BUTTON',
            });
        }

        function handleClickVolume(muted: boolean) {
            log({
                type: 'CLICK',
                event_name: 'MUTE_BUTTON',
                event_value: {
                    click_type: muted ? 'MUTE' : 'NOT_MUTE',
                },
            });
            emit('click-volume', muted);
        }

        watch(() => state.showError, val => {
            emit('play-error', val);
        }, {
            immediate: true,
        });

        function retryVideo(time: number) {
            kernelConfig.startFromSec = time;
            if (showDegradeTips.value) {
                showDegradeTips.value = false;
            } else {
                player.value?.reload();
            }
            state.showError = false;
        }

        function handleVolumeChange(value: number) {
            emit('volume-change', value);
        }

        return {
            player,
            kernelConfig,
            ie,
            playerTime,
            KwaiPlayerKernel,
            useVideoPlay,
            ...toRefs(state),
            handlePause,
            handlePlay,
            handleEnded,
            handleSeeked,
            handleError,
            retryVideo,
            handleLoadStart,
            fullscreenChange,
            handleVolumeChange,
            handleClickVolume,
            loadVideo,
            canShowBackPoster,
        };
    },
});
